import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import { Company, CompanyUser } from '@/types';
import { api } from '@/services/api';
import { isValidEmail } from '@/lib/formatters';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { type DeleteStep, type TeamMemberDraft } from '../types';

interface DashboardParams {
  company: Company;
  onForceRefresh?: () => Promise<void>;
}

interface PaginationResult<T> {
  items: T[];
  currentPage: number;
  totalCount: number;
  totalPages: number;
}

const createInitialTeamMemberDraft = (): TeamMemberDraft => ({
  name: '',
  email: '',
  receiveNotifications: true
});

const paginateItems = <T>(items: T[], page: number, pageSize: number) => {
  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);

  return {
    totalCount,
    totalPages,
    currentPage,
    items: items.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  };
};

interface UseCompanyDashboardTeamState {
  isPrimaryAccount: boolean;
  teamMembers: CompanyUser[];
  teamPagination: PaginationResult<CompanyUser>;
  isAddingTeamMember: boolean;
  newMember: TeamMemberDraft;
  setNewMember: Dispatch<SetStateAction<TeamMemberDraft>>;
  memberError: string;
  isSavingMember: boolean;
  editingMember: CompanyUser | null;
  setEditingMember: Dispatch<SetStateAction<CompanyUser | null>>;
  teamEditError: string;
  isSavingTeamEdit: boolean;
  resettingMemberId: string | null;
  memberToDelete: CompanyUser | null;
  deleteStep: DeleteStep;
  setTeamPage: Dispatch<SetStateAction<number>>;
  teamPageSize: number;
  setTeamPageSizeAndReset: (size: number) => void;
  openAddTeamMember: () => void;
  closeAddTeamMember: () => void;
  handleAddTeamMember: () => Promise<void>;
  closeEditMember: () => void;
  handleSaveTeamMember: () => Promise<void>;
  handleResetMemberPassword: (member: CompanyUser) => Promise<void>;
  openDeleteMember: (member: CompanyUser) => void;
  closeDeleteMember: () => void;
  executeDelete: () => Promise<void>;
}

export const useCompanyDashboardTeam = ({
  company,
  onForceRefresh
}: DashboardParams): UseCompanyDashboardTeamState => {
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [newMember, setNewMember] = useState<TeamMemberDraft>(createInitialTeamMemberDraft);
  const [isSavingMember, setIsSavingMember] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [editingMember, setEditingMember] = useState<CompanyUser | null>(null);
  const [teamEditError, setTeamEditError] = useState('');
  const [isSavingTeamEdit, setIsSavingTeamEdit] = useState(false);
  const [resettingMemberId, setResettingMemberId] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<DeleteStep>('idle');
  const [memberToDelete, setMemberToDelete] = useState<CompanyUser | null>(null);
  const deleteStepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPrimaryAccount = company.loggedInMember?.isPrimary ?? true;
  const teamMembers = company.teamMembers ?? [];
  const teamPagination = paginateItems(teamMembers, teamPage, teamPageSize);
  const refreshCompanyData = async () => {
    if (!onForceRefresh) return;
    await onForceRefresh();
  };

  useEffect(() => {
    setTeamPage(1);
  }, [teamMembers.length]);

  useEffect(() => {
    setTeamEditError('');
  }, [editingMember?.id]);

  useEffect(() => {
    return () => {
      if (deleteStepTimeoutRef.current) {
        clearTimeout(deleteStepTimeoutRef.current);
      }
    };
  }, []);

  const closeAddTeamMember = useCallback(() => {
    setIsAddingTeamMember(false);
    setNewMember(createInitialTeamMemberDraft());
    setMemberError('');
  }, []);

  const openAddTeamMember = useCallback(() => {
    setIsAddingTeamMember(true);
    setNewMember(createInitialTeamMemberDraft());
    setMemberError('');
  }, []);

  const closeEditMember = useCallback(() => {
    setEditingMember(null);
    setTeamEditError('');
  }, []);

  const handleAddTeamMember = useCallback(async () => {
    const name = newMember.name.trim();
    const email = newMember.email.trim().toLowerCase();

    if (!name || !email) {
      setMemberError('Please provide both name and email.');
      return;
    }
    if (!isValidEmail(email)) {
      setMemberError('Please enter a valid email address.');
      return;
    }

    setIsSavingMember(true);
    setMemberError('');
    try {
      await api.createCompanyMember(company.id, {
        name,
        email,
        receiveNotifications: newMember.receiveNotifications
      });

      showAlertModal('Team member added! A temporary password has been emailed to them.');
      closeAddTeamMember();
      await refreshCompanyData();
    } catch (e: unknown) {
      setMemberError(normalizeErrorMessage(e, 'Failed to add team member.'));
    } finally {
      setIsSavingMember(false);
    }
  }, [newMember, closeAddTeamMember, company.id, refreshCompanyData]);

  const handleSaveTeamMember = useCallback(async () => {
    if (!editingMember) return;
    const name = editingMember.name.trim();
    const email = editingMember.email.trim().toLowerCase();
    if (!name || !email) {
      setTeamEditError('Please provide both name and email.');
      return;
    }
    if (!isValidEmail(email)) {
      setTeamEditError('Please enter a valid email address.');
      return;
    }

    setIsSavingTeamEdit(true);
    setTeamEditError('');
    try {
      await api.updateCompanyMember(editingMember.id, {
        name,
        email,
        receiveNotifications: editingMember.receiveNotifications
      });
      await refreshCompanyData();
      closeEditMember();
    } catch (e: unknown) {
      setTeamEditError(normalizeErrorMessage(e, 'Failed to update team member.'));
    } finally {
      setIsSavingTeamEdit(false);
    }
  }, [editingMember, closeEditMember, refreshCompanyData]);

  const handleResetMemberPassword = useCallback(
    async (member: CompanyUser) => {
      if (!isPrimaryAccount) {
        showAlertModal('Only the primary contact can send password resets.');
        return;
      }

      const confirmed = await showConfirmModal(
        `Send a password reset email to ${member.name} (${member.email})?`,
        {
          title: 'Send Password Reset',
          confirmText: 'Send',
          cancelText: 'Cancel',
          isDestructive: false
        }
      );
      if (!confirmed) return;

      setResettingMemberId(member.id);
      try {
        await api.resetCompanyMemberPassword(member.id);
        showAlertModal('Reset email sent.');
      } catch (e: unknown) {
        showAlertModal(normalizeErrorMessage(e, 'Failed to send reset email.'));
      } finally {
        setResettingMemberId(null);
      }
    },
    [isPrimaryAccount]
  );

  const executeDelete = useCallback(async () => {
    if (!memberToDelete) return;

    setDeleteStep('processing');
    try {
      await api.deleteCompanyMember(memberToDelete.id);
      setDeleteStep('verifying');
      await new Promise((resolve) => setTimeout(resolve, 3000));
      setDeleteStep('success');
      await refreshCompanyData();

      if (deleteStepTimeoutRef.current) {
        clearTimeout(deleteStepTimeoutRef.current);
      }
      deleteStepTimeoutRef.current = setTimeout(() => {
        setDeleteStep('idle');
        setMemberToDelete(null);
        deleteStepTimeoutRef.current = null;
      }, 1500);
    } catch (e: unknown) {
      showAlertModal(`Error during deletion: ${normalizeErrorMessage(e, 'Unknown error')}`);
      setDeleteStep('idle');
    }
  }, [memberToDelete, refreshCompanyData]);

  const openDeleteMember = useCallback((member: CompanyUser) => {
    setMemberToDelete(member);
    setDeleteStep('confirming');
  }, []);

  const closeDeleteMember = useCallback(() => {
    if (deleteStepTimeoutRef.current) {
      clearTimeout(deleteStepTimeoutRef.current);
      deleteStepTimeoutRef.current = null;
    }
    setMemberToDelete(null);
    setDeleteStep('idle');
  }, []);

  const setTeamPageSizeAndReset = useCallback((size: number) => {
    setTeamPageSize(size);
    setTeamPage(1);
  }, []);

  return {
    isPrimaryAccount,
    teamMembers,
    teamPagination,
    isAddingTeamMember,
    newMember,
    setNewMember,
    memberError,
    isSavingMember,
    editingMember,
    setEditingMember,
    teamEditError,
    isSavingTeamEdit,
    resettingMemberId,
    memberToDelete,
    deleteStep,
    setTeamPage,
    teamPageSize,
    setTeamPageSizeAndReset,
    openAddTeamMember,
    closeAddTeamMember,
    handleAddTeamMember,
    closeEditMember,
    handleSaveTeamMember,
    handleResetMemberPassword,
    openDeleteMember,
    closeDeleteMember,
    executeDelete
  };
};
